import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { FaComments, FaTimes, FaPaperPlane, FaTrash, FaRobot, FaUser, FaHistory, FaChevronDown, FaExpand, FaCompress } from 'react-icons/fa';
import { HiSparkles } from 'react-icons/hi';
import { useTranslation } from 'react-i18next';
import { apiRequest, API_ENDPOINTS } from '../config/api';
import toast from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import TutorialCard from './TutorialCard';
import { useUser } from '../contexts/UserContext';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp?: Date;
  tutorialCard?: any; // Tutorial card data if present
}

interface Conversation {
  id: string;
  title: string;
  lastMessageAt: string;
  _count: {
    messages: number;
  };
}

interface FloatingChatWidgetProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function FloatingChatWidget({ isOpen, onOpenChange }: FloatingChatWidgetProps) {
  const { t, i18n } = useTranslation('common');
  const location = useLocation();
  const { user, isAdmin, isCorporateManager, isCorporateMember, selectedAdminAccount, selectedCorporateFarm, selectedFarm } = useUser();
  const [isExpanded, setIsExpanded] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Get effective farmId based on user role (same logic as other pages)
  const isCorporate = user?.role === 'CORPORATE' || isCorporateManager || isCorporateMember;
  const effectiveFarmId = isCorporate 
    ? (selectedCorporateFarm?.farm?.id || null)
    : (isAdmin 
        ? (selectedAdminAccount?.farm?.id || null) 
        : (selectedFarm?.farmId || user?.farmId || null));

  // Get current page context for better AI responses
  const getPageContext = () => {
    const path = location.pathname;
    const pageContexts: { [key: string]: string } = {
      '/dashboard': 'Dashboard - Overview of farm operations',
      '/rations': 'Rations - Create and manage feeding recipes',
      '/inventory': 'Inventory - Track feed items and stock',
      '/feed-list': 'Feed List - Browse feed library',
      '/file-center': 'File Center - Manage files and documents',
      '/records-and-charts': 'Records & Charts - View historical data and analytics',
      '/help': 'Help - Get support and documentation',
      '/account': 'My Account - Account settings',
      '/notifications': 'Notifications - View notifications',
      '/notification-settings': 'Notification Settings - Configure notifications',
      '/settings': 'Settings - Farm and account settings',
    };
    
    return pageContexts[path] || 'Feed Cowdemy';
  };

  // Load conversation history when widget opens
  useEffect(() => {
    if (isOpen) {
      loadConversations();
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
  }, [isOpen]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadConversations = async () => {
    try {
      setLoadingHistory(true);
      const response = await apiRequest<{ conversations: Conversation[] }>(
        API_ENDPOINTS.AI_ASSISTANT_CONVERSATIONS,
        'GET'
      );
      setConversations(response.conversations);
    } catch (error) {
      console.error('Error loading conversations:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const loadConversation = async (id: string) => {
    try {
      setLoading(true);
      const response = await apiRequest<{
        conversation: {
          id: string;
          title: string;
          messages: Message[];
        };
      }>(API_ENDPOINTS.AI_ASSISTANT_CONVERSATION.replace(':id', id), 'GET');

      setConversationId(response.conversation.id);
      setMessages(
        response.conversation.messages.map((msg: any) => ({
          role: msg.role,
          content: msg.content,
          timestamp: msg.createdAt ? new Date(msg.createdAt) : undefined,
        }))
      );
      setShowHistory(false);
    } catch (error: any) {
      console.error('Error loading conversation:', error);
      toast.error('Failed to load conversation');
    } finally {
      setLoading(false);
    }
  };

  const deleteConversation = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await apiRequest(API_ENDPOINTS.AI_ASSISTANT_CONVERSATION.replace(':id', id), 'DELETE');
      setConversations(conversations.filter((c) => c.id !== id));
      if (conversationId === id) {
        setMessages([]);
        setConversationId(null);
      }
      toast.success('Conversation deleted');
    } catch (error: any) {
      console.error('Error deleting conversation:', error);
      toast.error('Failed to delete conversation');
    }
  };

  const startNewChat = () => {
    setMessages([]);
    setConversationId(null);
    setShowHistory(false);
  };

  const handleSend = async () => {
    if (!inputValue.trim() || loading) return;

    const userMessage = inputValue.trim();
    setInputValue('');
    
    // Add user message to UI immediately
    const newUserMessage: Message = {
      role: 'user',
      content: userMessage,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, newUserMessage]);

    setLoading(true);

    try {
      const response = await apiRequest<{
        response: string;
        conversationId: string;
        tokensUsed: number;
        tutorialCard?: any;
      }>(API_ENDPOINTS.AI_ASSISTANT_CHAT, 'POST', {
        message: userMessage,
        conversationId,
        pageContext: getPageContext(), // Include current page context
        userLanguage: i18n.language.split('-')[0], // Include current language (en, fr, es) - use base language code
        farmId: effectiveFarmId // Include farmId so AI can access farm-specific data
      });

      setConversationId(response.conversationId);

      // Add AI response
      const aiMessage: Message = {
        role: 'assistant',
        content: response.response,
        timestamp: new Date(),
        tutorialCard: response.tutorialCard || null // Capture tutorial card
      };
      setMessages(prev => [...prev, aiMessage]);
    } catch (error: any) {
      console.error('Chat error:', error);
      toast.error(error.message || 'Failed to send message');
      
      // Remove the user message on error
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!isOpen) {
    return null;
  }

  const chatContent = (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, scale: 0.8, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.8, y: 20 }}
        transition={{ type: "spring", damping: 20 }}
        className={`fixed ${isExpanded ? 'inset-4' : 'bottom-6 right-6 w-96 h-[600px]'} z-50 bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden transition-all duration-300`}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 flex justify-between items-center shadow-lg">
          <div className="flex items-center gap-3">
            <div className="relative">
              <FaRobot className="text-2xl animate-pulse" />
              <span className="absolute -bottom-0.5 -right-0.5 bg-green-400 rounded-full w-2.5 h-2.5 border-2 border-blue-600"></span>
            </div>
            <div>
              <h3 className="font-bold text-lg flex items-center gap-2">
                <HiSparkles className="text-yellow-300" />
                {t('aiAssistant.title', 'AI Assistant')}
              </h3>
              <p className="text-xs opacity-90 font-medium">{t('aiAssistant.subtitle', 'Get help with Feed Cowdemy')}</p>
            </div>
          </div>
          <div className="flex gap-1">
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => setShowHistory(!showHistory)}
              className="p-2 hover:bg-blue-800 rounded-lg transition-colors"
              aria-label="Conversation history"
            >
              <FaHistory size={16} />
            </motion.button>
            {messages.length > 0 && (
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={startNewChat}
                className="p-2 hover:bg-blue-800 rounded-lg transition-colors"
                aria-label="New chat"
              >
                <FaComments size={14} />
              </motion.button>
            )}
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-2 hover:bg-blue-800 rounded-lg transition-colors"
              aria-label={isExpanded ? t('aiAssistant.compress', 'Compress') : t('aiAssistant.expand', 'Expand')}
            >
              {isExpanded ? <FaCompress size={16} /> : <FaExpand size={16} />}
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => onOpenChange(false)}
              className="p-2 hover:bg-blue-800 rounded-lg transition-colors"
              aria-label={t('aiAssistant.minimize', 'Minimize')}
            >
              <FaChevronDown size={16} />
            </motion.button>
          </div>
        </div>

        {/* History Sidebar */}
        {showHistory && (
          <div className="absolute inset-0 bg-white z-10">
            <div className="p-4 border-b bg-gradient-to-r from-blue-600 to-blue-700 text-white">
              <div className="flex items-center justify-between">
                <h3 className="font-bold text-lg flex items-center gap-2">
                  <FaHistory />
                  Previous Chats
                </h3>
                <button
                  onClick={() => setShowHistory(false)}
                  className="p-2 hover:bg-blue-800 rounded-lg transition-colors"
                >
                  <FaTimes size={18} />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto scrollbar-modern" style={{ scrollbarWidth: 'thin', scrollbarColor: '#93c5fd transparent' }}>
              {loadingHistory ? (
                <div className="flex justify-center items-center h-32">
                  <div className="flex gap-1.5">
                    <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" />
                    <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0.15s' }} />
                    <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0.3s' }} />
                  </div>
                </div>
              ) : conversations.length === 0 ? (
                <div className="text-center text-gray-500 mt-12 p-4">
                  <FaHistory className="text-4xl mx-auto mb-4 opacity-20" />
                  <p className="text-sm">No previous conversations yet</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {conversations.map((conv) => (
                    <div
                      key={conv.id}
                      onClick={() => loadConversation(conv.id)}
                      className="px-4 py-2.5 hover:bg-blue-50 cursor-pointer transition-colors group border-l-2 border-transparent hover:border-blue-500"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <div className="w-1.5 h-1.5 rounded-full bg-blue-500 flex-shrink-0"></div>
                            <p className="font-medium text-sm text-gray-900 truncate group-hover:text-blue-600">
                              {conv.title}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 mt-0.5 ml-3.5">
                            <span className="text-xs text-gray-400">
                              {conv._count.messages} msg
                            </span>
                            <span className="text-gray-300">•</span>
                            <span className="text-xs text-gray-400">
                              {new Date(conv.lastMessageAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </span>
                          </div>
                        </div>
                        <button
                          onClick={(e) => deleteConversation(conv.id, e)}
                          className="p-1.5 hover:bg-red-100 rounded-lg text-gray-400 hover:text-red-600 transition-colors opacity-0 group-hover:opacity-100 flex-shrink-0"
                        >
                          <FaTrash size={12} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-gradient-to-b from-gray-50 to-white scrollbar-modern" style={{ scrollbarWidth: 'thin', scrollbarColor: '#93c5fd transparent' }}>
          {messages.length === 0 && !showHistory && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center text-gray-500 mt-12"
            >
              <div className="relative inline-block mb-4">
                <HiSparkles className="text-6xl mx-auto opacity-20 animate-pulse" />
                <FaRobot className="text-4xl mx-auto absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-blue-600" />
              </div>
              <p className="font-medium text-base mb-2">Welcome to AI Assistant!</p>
              <p className="text-sm opacity-75">{t('aiAssistant.welcome', 'Ask me anything about Feed Cowdemy!')}</p>
            </motion.div>
          )}
          {messages.map((msg, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className={`flex items-start gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              {msg.role === 'assistant' && (
                <div className="flex-shrink-0 mt-1">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-md">
                    <FaRobot className="text-white text-sm" />
                  </div>
                </div>
              )}
              <div className="max-w-[75%]">
                <div
                  className={`rounded-2xl px-3 py-2 shadow-sm ${
                    msg.role === 'user'
                      ? 'bg-gradient-to-br from-blue-600 to-blue-700 text-white rounded-tr-none'
                      : 'bg-white text-gray-800 border border-gray-200 rounded-tl-none'
                  }`}
                >
                  {msg.role === 'assistant' ? (
                    <div className="text-sm leading-tight prose prose-sm max-w-none prose-headings:font-semibold prose-headings:text-gray-800 prose-headings:mb-1 prose-headings:mt-1 prose-p:text-gray-800 prose-p:my-1 prose-strong:text-gray-900 prose-strong:font-semibold prose-ul:text-gray-800 prose-ol:text-gray-800 prose-li:text-gray-800 prose-li:my-0.5 prose-code:text-blue-700 prose-code:bg-blue-50 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-a:text-blue-600 prose-a:font-medium hover:prose-a:text-blue-800">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {msg.content}
                      </ReactMarkdown>
                    </div>
                  ) : (
                    <p className="text-sm whitespace-pre-wrap leading-tight">{msg.content}</p>
                  )}
                </div>
                {msg.role === 'assistant' && msg.tutorialCard && (
                  <TutorialCard {...msg.tutorialCard} />
                )}
              </div>
              {msg.role === 'user' && (
                <div className="flex-shrink-0 mt-1">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-gray-600 to-gray-700 flex items-center justify-center shadow-md">
                    <FaUser className="text-white text-sm" />
                  </div>
                </div>
              )}
            </motion.div>
          ))}
          {loading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex justify-start items-start gap-2"
            >
              <div className="flex-shrink-0 mt-1">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-md">
                  <FaRobot className="text-white text-sm animate-pulse" />
                </div>
              </div>
              <div className="bg-white rounded-2xl rounded-tl-none px-4 py-3 shadow-sm border border-gray-200">
                <div className="flex gap-1.5">
                  <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" />
                  <div 
                    className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" 
                    style={{ animationDelay: '0.15s' }} 
                  />
                  <div 
                    className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" 
                    style={{ animationDelay: '0.3s' }} 
                  />
                </div>
              </div>
            </motion.div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="p-4 border-t bg-gradient-to-r from-gray-50 to-white">
          <div className="flex gap-2 items-end">
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={t('aiAssistant.placeholder', 'Ask a question...')}
              className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-white text-gray-800 placeholder-gray-400 disabled:bg-gray-100"
              disabled={loading}
            />
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleSend}
              disabled={!inputValue.trim() || loading}
              className="bg-gradient-to-br from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-5 py-3 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center shadow-md hover:shadow-lg disabled:shadow-none"
              aria-label={t('aiAssistant.send', 'Send message')}
            >
              <FaPaperPlane className={loading ? 'animate-pulse' : ''} />
            </motion.button>
          </div>
          <p className="text-xs text-gray-500 mt-2 text-center">
            {t('aiAssistant.disclaimer', 'AI can make mistakes. Please verify important information.')}
          </p>
        </div>
      </motion.div>
    </AnimatePresence>
  );

  return createPortal(chatContent, document.body);
}

